import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCachedAnimationDetailsById,
  selectCachedAnimationRenderById,
  setCachedAnimationDetails,
  setCachedAnimationRender,
  unloadedAnimation,
} from "../../../app/slices/AnimationsSlice";
import { selectCurrentOrganization } from "../../../app/slices/UserSlice";
import { RootState } from "../../../app/Store";
import { AnimationDetailsDTO } from "../../../dto/AnimationDetailsDTO";
import { AnimationService } from "../../../services/AnimationService";

/**
 * The single home for fetching animation details and previews by `externalId`.
 *
 * Cache semantics (see AnimationsSlice): `undefined` = not fetched yet,
 * `null` = the API answered 404, so the animation is unavailable and is never
 * requested again this session. A network error caches nothing, so a later
 * mount retries.
 */

export function useAnimationDetails(externalId?: string): {
  details: AnimationDetailsDTO | undefined;
  isUnavailable: boolean;
  isFetching: boolean;
} {
  const dispatch = useDispatch();
  const currentOrganization = useSelector(selectCurrentOrganization);
  const organizationId = currentOrganization?.id;
  const cached = useSelector((state: RootState) =>
    externalId ? selectCachedAnimationDetailsById(state, externalId) : undefined
  );
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (
      !externalId ||
      !organizationId ||
      externalId === unloadedAnimation.externalId ||
      cached !== undefined
    ) {
      // Nothing to fetch (or the cache was filled while a request was in
      // flight, which cancels that request below): make sure the flag is off.
      setIsFetching(false);
      return;
    }

    let isCancelled = false;
    setIsFetching(true);

    // Promise.resolve tolerates test doubles that return a plain value.
    Promise.resolve(
      AnimationService.GetAnimationDetails(organizationId, externalId)
    )
      .then((result) => {
        if (isCancelled) return;
        if (result?.status === "ok") {
          dispatch(
            setCachedAnimationDetails({ externalId, animationDetails: result.data })
          );
        } else if (result?.status === "notFound") {
          dispatch(setCachedAnimationDetails({ externalId, animationDetails: null }));
        }
      })
      .finally(() => {
        if (!isCancelled) setIsFetching(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [cached, dispatch, externalId, organizationId]);

  return {
    details: cached ?? undefined,
    isUnavailable: cached === null,
    isFetching,
  };
}

export function useAnimationRender(externalId?: string): {
  renderUri: string | undefined;
  isUnavailable: boolean;
  isFetching: boolean;
} {
  const dispatch = useDispatch();
  const currentOrganization = useSelector(selectCurrentOrganization);
  const organizationId = currentOrganization?.id;
  const cached = useSelector((state: RootState) =>
    externalId ? selectCachedAnimationRenderById(state, externalId) : undefined
  );
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (
      !externalId ||
      !organizationId ||
      externalId === unloadedAnimation.externalId ||
      cached !== undefined
    ) {
      setIsFetching(false);
      return;
    }

    let isCancelled = false;
    setIsFetching(true);

    Promise.resolve(
      AnimationService.GetAnimationPreview(organizationId, externalId)
    )
      .then((result) => {
        if (isCancelled) return;
        if (result?.status === "ok" && result.data?.downloadUri) {
          dispatch(
            setCachedAnimationRender({
              externalId,
              downloadUri: result.data.downloadUri,
            })
          );
        } else if (result?.status === "notFound") {
          dispatch(setCachedAnimationRender({ externalId, downloadUri: null }));
        }
      })
      .finally(() => {
        if (!isCancelled) setIsFetching(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [cached, dispatch, externalId, organizationId]);

  return {
    renderUri: cached ?? undefined,
    isUnavailable: cached === null,
    isFetching,
  };
}
